import axios from "axios"
import { sockets, ioInit } from '@/plugins/socketio.plugin'

const {
    VITE_APP_SERVER,
    VITE_APP_ANAME_PUBLIC_KEY,
    MODE
} = import.meta.env

const axiosInstance = axios.create({
    headers: {
        "x-application": "june07-blur",
    },
    withCredentials: true
})

const apiService = () => {
    const request = async (passedOptions) => {
        const defaultOptions = {
            method: 'GET'
        }
        const options = {
            ...defaultOptions,
            ...passedOptions
        }

        try {
            const response = await axiosInstance(options)
            return options.returnResponse ? response : response.data
        } catch (error) {
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.log(error.response.data)
                console.log(error.response.status)
                console.log(error.response.headers)
            } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                console.log(error.request)
            } else {
                // Something happened in setting up the request that triggered an Error
                console.log('Error', error.message)
            }
            console.log(error.config)
            throw error
        }
    }
    const authRequest = (options) => request({
        ...options,
        headers: {
            ...options.headers,
            'Authorization': `Bearer ${options.token}`
        }
    })

    const cache = new Map() // In-memory cache (could also be replaced by `localStorage` for persistence)
    const CACHE_LIFETIME = 5 * 60 * 1000 // Cache lifetime in milliseconds (5 minutes)
    const MAX_CACHE_SIZE = 100 // Max number of items in cache

    // Function to clean up expired or excess cache entries
    const cleanUpCache = () => {
        const now = Date.now()

        // Remove expired entries
        for (const [key, { timestamp }] of cache.entries()) {
            if (now - timestamp > CACHE_LIFETIME) {
                cache.delete(key) // Remove the expired entry
            }
        }

        // Remove the oldest entry if the cache exceeds the max size
        if (cache.size > MAX_CACHE_SIZE) {
            const oldestKey = cache.keys().next().value
            cache.delete(oldestKey) // Remove the oldest entry
        }
    }

    // Function to load cache from localStorage (if needed)
    const getCacheFromStorage = () => {
        const cacheData = localStorage.getItem('wsCache')
        return cacheData ? new Map(JSON.parse(cacheData)) : new Map()
    }

    // Function to store cache into localStorage
    const setCacheToStorage = () => {
        localStorage.setItem('wsCache', JSON.stringify([...cache]))
    }

    const wsEmit = (options) => {
        return new Promise(async resolve => {
            const { eventName, auth, ...args } = options
            const socketIndex = auth ? 1 : 0

            // Generate a cache key based on eventName and arguments
            const cacheKey = `${eventName}:${JSON.stringify(args)}`

            // Load cache from storage (if not already loaded)
            if (!cache.size) {
                Object.assign(cache, getCacheFromStorage())
            }

            // Clean up expired or excess cache entries before making a new request
            cleanUpCache()

            // Check if the response is in the cache
            if (cache.has(cacheKey)) {
                console.log('Returning cached response for', cacheKey)
                resolve(cache.get(cacheKey))
                return
            }

            try {
                // If auth is needed and no socket exists, initialize it
                if (auth && !sockets['sublet']?.[1]) {
                    await ioInit({ auth })
                }

                sockets['sublet']?.[socketIndex]?.emit(eventName, { ...args }, (json) => {
                    const { error } = json

                    if (error) {
                        console.error(error)
                    }

                    // Cache the response with the current timestamp
                    cache.set(cacheKey, { data: json, timestamp: Date.now() })

                    // Store the updated cache in localStorage
                    setCacheToStorage()

                    resolve(json)
                })
            } catch (error) {
                console.error(error)
                resolve({ error })
            }
        })
    }

    return {
        request,
        authRequest,
        apikeys: (token) => authRequest({ token, url: `${VITE_APP_SERVER}/v1/sublet/apikeys` }),
        assignSubletUsername: (token) => authRequest({ token, url: `${VITE_APP_SERVER}/v1/sublet/aname/${VITE_APP_ANAME_PUBLIC_KEY}` }),
        buildInfo: async () => {
            if (MODE !== 'production' && !/dev\./.test(window.location.host)) {
                return
            }
            try {
                const { data } = await axiosInstance.get(`${window.origin}/build-info.json`, {
                    headers: {
                        'Cache-Control': 'no-store, max-age=0',
                    },
                })
                return data
            } catch (error) {
                console.error(error)
            }
        },
        info: () => request({ url: `${VITE_APP_SERVER}/v1/info` }),
        domains: () => wsEmit({ eventName: 'domains' }),
        buy: (args) => wsEmit({ eventName: 'buy', ...args }),
        config: ({ auth, ...args }) => wsEmit({ eventName: 'config', auth, ...args }),
        completePaymentIntents: ({ auth }) => wsEmit({ eventName: 'completePaymentIntents', auth }),
        updateConfig: ({ auth, ...args }) => wsEmit({ eventName: 'updateConfig', auth, ...args }),
        authCheck: (args) => wsEmit({ eventName: 'authCheck', ...args }),
        authHook: ({ auth }) => wsEmit({ eventName: 'authHook', auth }),
    }
}

export default {
    async install(app) {
        app.config.globalProperties.$api = apiService()
    }
}